import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';

import {
  Constants,
  CoverageSNPTypes,
  IFP_TIERED_NETWORK,
  LOB,
} from '../../common/Constants';
import { PortalName } from '../../common/Portal';
import {
  GuestMemberInfo,
  GuestPortalMemberInfo,
} from '../../common/Portal/GuestPortalMemberInfo';
import {
  Coverage,
  CoverageEffDateRange,
  Eligibility,
  PortalMemberInfo,
  TypeCode,
} from '../../common/Portal/PortalMemberInfo';
import { getFeatureFlag } from '../../common/Utils/getFeatureFlag';
import { isCnsMember } from '../../common/Utils/memberUtils/memberUtils';
import { CLIENT_PORTAL_CONFIG_MAP, ClientPortalConfig } from './constants';

const PSX_BLUE_GREEN_CANARY_SESSION_STORAGE_KEY = 'psxBlueGreenCanaryColor';

const coverageTypeCodeToDescMapping = {
  M: 'MEDICAL',
  B: 'BEHAVIORAL HEALTH',
  D: 'DENTAL',
  V: 'VISION',
};
export const getPSXBlueGreenDeployment = (flags, searchParams) => {
  // NOTE: if you want to change the flag to a test flag in Launch Darkly
  // 1. Create a new flag in Launch Darkly (e.g. psx-blue-green-live-flag-test)
  // 2. Change the flag name below to the new test flag name (i.e. flags.psxBlueGreenLiveFlagTest)
  // Note that the flag name in Launch Darkly is converted to camel case when accessed in code.
  // const psxBlueGreenFlag = flags?.blueGreenLiveFlag;
  const psxBlueGreenFlag = flags?.guestExperienceTestFlag;

  document.cookie = `bgParcelsCookie=${psxBlueGreenFlag}; path=/;`;

  // Handle overrides for Blue/Green PSX
  const overrideBGFlag =
    searchParams.has('bgEnv') &&
    (searchParams.get('bgEnv') === 'blue' ||
      searchParams.get('bgEnv') === 'green' ||
      searchParams.get('bgEnv') === 'live')
      ? searchParams.get('bgEnv')
      : undefined;

  if (overrideBGFlag) {
    document.cookie = `bgTestingEnv=${overrideBGFlag}; path=/;`;
  }
  const bgTestingEnv = storage.cookie.get('bgTestingEnv');
  const coloredDeploymentFlag =
    overrideBGFlag || bgTestingEnv || psxBlueGreenFlag;

  if (coloredDeploymentFlag === 'blue' || coloredDeploymentFlag === 'green') {
    storage.session.set(
      PSX_BLUE_GREEN_CANARY_SESSION_STORAGE_KEY,
      coloredDeploymentFlag
    );
    return coloredDeploymentFlag;
  } else {
    return undefined;
  }
};

// refer to https://github.com/uhc-tech-provider/search-container/blob/main/products/web/src/routes/FindCare/FindCare.tsx
// for an example of how to set the graphql URL based on blue/green canary deployment.

export const getBlueGreenGQLApiUrl = (flags, searchParams) => {
  const psxGuestFlag = getPSXBlueGreenDeployment(flags, searchParams);
  return psxGuestFlag
    ? `/${psxGuestFlag}${config('GRAPHQL_API_URL')}`
    : config('GRAPHQL_API_URL');
};

export function isEniNiceMember(
  lineOfBusiness: string,
  searchDirectory: string = '',
  planCategory: string = '',
  eniNicePlanCategoryName: string = ''
): boolean {
  const isEniNiceByLobAndDirectory =
    lineOfBusiness === LOB.ENI &&
    searchDirectory === Constants.MEMBER_TYPES.NICE;
  const isEniNiceByPlanCategory = !!(
    lineOfBusiness === LOB.ENI &&
    planCategory &&
    eniNicePlanCategoryName
      .split(',')
      .some((n) => n.trim() === planCategory.trim())
  );
  return isEniNiceByLobAndDirectory || isEniNiceByPlanCategory;
}

export const PSXBlueGreenCanaryGraphqlApi = () => {
  // get launchdarkly flags
  const coloredDeploymentFlag = storage.session.get(
    PSX_BLUE_GREEN_CANARY_SESSION_STORAGE_KEY
  );

  if (coloredDeploymentFlag) {
    return `/${coloredDeploymentFlag}${config('GRAPHQL_API_URL')}`;
  } else {
    return config('GRAPHQL_API_URL');
  }
};

export function getMembershipCategory(
  lineOfBusiness: string,
  searchDirectory?: string
): string | undefined {
  if (lineOfBusiness === LOB.IFP) {
    return Constants.MEMBERSHIP_CATEGORY.PUBLIC_EXCHANGE;
  }
  if (lineOfBusiness === LOB.SHOP) {
    return Constants.MEMBERSHIP_CATEGORY.PUBLIC_EXCHANGE;
  }
  if (
    searchDirectory === Constants.MEMBER_TYPES.USP ||
    isEniNiceMember(lineOfBusiness, searchDirectory)
  ) {
    return Constants.MEMBERSHIP_CATEGORY.OXFORD;
  }
  return undefined;
}

export function getSnpType(searchDirectory?: string): string | undefined {
  if (searchDirectory?.toLowerCase() === CoverageSNPTypes.DSNP.toLowerCase()) {
    return CoverageSNPTypes.DSNP;
  }

  return undefined;
}

// refer to https://github.com/uhc-tech-provider/search-container/blob/main/products/web/src/common/Layouts/CommonHeader/CommonHeader.tsx
// for an example of how to set the parcel URL based on blue/green canary deployment.
export const PSXBlueGreenCanaryParcelUrl = (flags, searchParams) => {
  // Handle overrides for Blue/Green PSX
  const coloredDeploymentFlag = getPSXBlueGreenDeployment(flags, searchParams);

  if (coloredDeploymentFlag) {
    return config(`PSX_PARCEL_URL_${coloredDeploymentFlag.toUpperCase()}`);
  } else {
    return config('PSX_PARCEL_URL');
  }
};

export const populatePortalDataFromGuestPlanPicker = (
  lineOfBusiness: string,
  reciprocityId: string,
  population: string,
  coverageTypeCode: string,
  coverageEffStartDate: string,
  coverageEffEndDate: string,
  stateCode: string,
  planName?: string,
  planYear?: string,
  searchDirectory?: string,
  planCategory?: string,
  guestPlanIdentifier?: string,
  ddpCode?: string,
  rulesPackageKey?: string,
  policyNumber?: string,
  pvrcode?: string,
  eniNicePlanCategoryName?: string
): GuestPortalMemberInfo | PortalMemberInfo => {
  const guestCoverageEffDateRange: CoverageEffDateRange = {
    startDate: coverageEffStartDate,
    endDate: coverageEffEndDate,
  };

  const guestTypeCode: TypeCode = {
    desc: coverageTypeCodeToDescMapping[coverageTypeCode] ?? '',
    coverageTypeCode,
    reciprocityId,
  };

  const snpType = getSnpType(searchDirectory);

  const guestCoverageType: Coverage = {
    coverageEffDateRange: guestCoverageEffDateRange,
    typeCode: guestTypeCode,
    reciprocityId,
    situsState: stateCode,
    snpType,
    planVariationCode: pvrcode,
  };

  const guestEligibility: Eligibility = {
    memberHealthCoverage: {
      coverageType: [guestCoverageType],
    },
  };
  const membershipCategory: string | undefined = getMembershipCategory(
    lineOfBusiness,
    searchDirectory
  );
  let isIFPMember: boolean | undefined = undefined;
  let isIFPTieredPlan: boolean | undefined = undefined;
  if (lineOfBusiness === LOB.IFP) {
    isIFPMember = true;
    isIFPTieredPlan = reciprocityId === IFP_TIERED_NETWORK;
  }
  // Determine if guest is selecting a future year (pre-effective)
  const currentYear = new Date().getFullYear();
  const selectedYear = planYear ? parseInt(planYear, 10) : currentYear;
  const isPreEffective = selectedYear > currentYear;

  const isUspNiceMember = isEniNiceMember(
    lineOfBusiness,
    searchDirectory,
    planCategory,
    eniNicePlanCategoryName
  );

  const guestMemberInfo: GuestMemberInfo = {
    eligibility: [guestEligibility],
    lineOfBusiness,
    membershipCategory,
    population,
    reciprocityId,
    isCnSMember: isCnsMember(lineOfBusiness),
    isIFPMember,
    isIFPTieredPlan,
    planName,
    planYear,
    planCategory,
    searchDirectory,
    policyNumber,
    guestPlanIdentifier,
    ddpCode,
    rulesPackageKey,
    isPreEffective,
    isUspNiceMember,
  };
  return {
    memberInfo: [guestMemberInfo],
  };
};

export const isDualYearCheckEnabledForGuest = (
  lob: string,
  featureFlags: { key: string; active: boolean }[],
  selectedPlan: { medicarePlanType?: string; years?: string[] }
): boolean => {
  const { medicarePlanType, years } = selectedPlan;
  const hasDualYears = years && Array.isArray(years) && years.length > 1;

  if (!hasDualYears) {
    return false;
  }

  const constructFlagName = (): string => {
    if (medicarePlanType) {
      return `GUEST_SHOW_${lob}_${medicarePlanType}_DUAL_YEAR_SCREEN`;
    }
    return `GUEST_SHOW_${lob}_DUAL_YEAR_SCREEN`;
  };

  const flagName = constructFlagName();

  return flagName ? getFeatureFlag(featureFlags, flagName) : false;
};

export const clearGuestSession = () => {
  sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS);
  sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.PORTAL_STATE);
  sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION);
};

export const PORTAL_STATE_SYNC_CHANNEL = 'portal-state-sync';
const PORTAL_STATE_REQUEST_EVENT = 'request-portal-state';
const PORTAL_STATE_RESPONSE_EVENT = 'portal-state-response';
const PORTAL_STATE_OPENER_RESPONSE_TIMEOUT_MS = 1000;

export const parsePortalState = <T = Record<string, unknown>>(
  value: string | null
): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const normalizeUrl = (value: string): string => {
  try {
    const parsed = new URL(value);
    return `${parsed.origin}${parsed.pathname}${parsed.search}`;
  } catch {
    return value;
  }
};

export const isMatchingPortalStateSource = (
  sourceReferrer: string,
  currentUrl: string
): boolean => {
  if (!sourceReferrer) return true;
  return normalizeUrl(sourceReferrer) === normalizeUrl(currentUrl);
};

export const createPortalStateRequestId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getSessionPortalState = <
  T = Record<string, unknown>
>(): T | null =>
  parsePortalState<T>(
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.PORTAL_STATE)
  );

export const setSessionPortalState = (portalState: unknown) => {
  sessionStorage.setItem(
    Constants.STORAGE_KEYS.SESSION.PORTAL_STATE,
    JSON.stringify(portalState)
  );
};

export const setupPortalStateResponder = (): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const channel =
    'BroadcastChannel' in window
      ? new BroadcastChannel(PORTAL_STATE_SYNC_CHANNEL)
      : null;

  const respondWithPortalState = (
    requestId: string,
    respond: (payload: Record<string, unknown>) => void,
    sourceReferrer = ''
  ) => {
    if (!isMatchingPortalStateSource(sourceReferrer, window.location.href)) {
      return;
    }

    const portalState = getSessionPortalState<Record<string, unknown>>();
    if (!portalState) {
      return;
    }

    respond(portalState);
  };

  const handleWindowMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      return;
    }

    if (event.data?.type !== PORTAL_STATE_REQUEST_EVENT) {
      return;
    }

    const requestId =
      typeof event.data?.requestId === 'string' ? event.data.requestId : null;
    if (!requestId || !event.source || !('postMessage' in event.source)) {
      return;
    }

    const sourceReferrer =
      typeof event.data?.sourceReferrer === 'string'
        ? event.data.sourceReferrer
        : '';

    respondWithPortalState(
      requestId,
      (payload) => {
        (event.source as WindowProxy).postMessage(
          {
            type: PORTAL_STATE_RESPONSE_EVENT,
            requestId,
            payload,
          },
          event.origin
        );
      },
      sourceReferrer
    );
  };

  window.addEventListener('message', handleWindowMessage);

  if (channel) {
    channel.onmessage = (event) => {
      if (event.data?.type !== PORTAL_STATE_REQUEST_EVENT) {
        return;
      }

      const requestId =
        typeof event.data?.requestId === 'string' ? event.data.requestId : null;
      if (!requestId) {
        return;
      }

      const sourceReferrer =
        typeof event.data?.sourceReferrer === 'string'
          ? event.data.sourceReferrer
          : '';

      respondWithPortalState(
        requestId,
        (payload) => {
          channel.postMessage({
            type: PORTAL_STATE_RESPONSE_EVENT,
            requestId,
            payload,
          });
        },
        sourceReferrer
      );
    };
  }

  return () => {
    window.removeEventListener('message', handleWindowMessage);
    if (channel) {
      channel.close();
    }
  };
};

export const requestPortalStateFromSession = (
  onSyncedState: (state: Record<string, unknown>) => void
): (() => void) | undefined => {
  const existing = getSessionPortalState<Record<string, unknown>>();
  if (existing) {
    onSyncedState(existing);
    return undefined;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  const hasOpener = !!window.opener && window.opener !== window;
  const canUseBroadcast = 'BroadcastChannel' in window;
  if (!hasOpener && !canUseBroadcast) {
    return undefined;
  }

  const requestId = createPortalStateRequestId();
  const sourceReferrer = document.referrer || null;
  let isResolved = false;
  let fallbackTimeout: ReturnType<typeof setTimeout> | undefined;
  let channel: BroadcastChannel | null = null;

  const resolveWithState = (payload: unknown) => {
    if (isResolved) {
      return;
    }

    isResolved = true;
    if (fallbackTimeout) {
      clearTimeout(fallbackTimeout);
    }
    setSessionPortalState(payload);
    onSyncedState(payload as Record<string, unknown>);
    if (channel) {
      channel.close();
      channel = null;
    }
    window.removeEventListener('message', handleWindowMessage);
  };

  const handleWindowMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      return;
    }

    if (
      event.data?.type === PORTAL_STATE_RESPONSE_EVENT &&
      event.data?.requestId === requestId
    ) {
      resolveWithState(event.data.payload);
    }
  };

  const startBroadcastFallback = () => {
    if (isResolved || !('BroadcastChannel' in window)) {
      return;
    }

    channel = new BroadcastChannel(PORTAL_STATE_SYNC_CHANNEL);

    channel.onmessage = (event) => {
      if (
        event.data?.type === PORTAL_STATE_RESPONSE_EVENT &&
        event.data?.requestId === requestId
      ) {
        resolveWithState(event.data.payload);
      }
    };

    channel.postMessage({
      type: PORTAL_STATE_REQUEST_EVENT,
      requestId,
      sourceReferrer,
    });
  };

  window.addEventListener('message', handleWindowMessage);

  if (hasOpener) {
    try {
      window.opener.postMessage(
        {
          type: PORTAL_STATE_REQUEST_EVENT,
          requestId,
          sourceReferrer,
        },
        window.location.origin
      );
      // When opener exists, only accept state from opener to avoid wrong-tab races.
      fallbackTimeout = setTimeout(() => {
        if (isResolved) {
          return;
        }
        isResolved = true;
        window.removeEventListener('message', handleWindowMessage);
      }, PORTAL_STATE_OPENER_RESPONSE_TIMEOUT_MS);
    } catch {
      window.removeEventListener('message', handleWindowMessage);
    }
  } else if (canUseBroadcast) {
    startBroadcastFallback();
  }

  return () => {
    isResolved = true;
    if (fallbackTimeout) {
      clearTimeout(fallbackTimeout);
    }
    window.removeEventListener('message', handleWindowMessage);
    if (channel) {
      channel.close();
    }
  };
};

const parseClientName = (value: string): string => {
  try {
    const parsed = JSON.parse(value);
    return parsed?.v || value;
  } catch {
    return value;
  }
};

export const isLawwGuest = () => {
  const storedPortalName = storage.session
    .get(Constants.STORAGE_KEYS.SESSION.PORTAL_NAME)
    ?.toLowerCase();

  return storedPortalName === PortalName.LAWW_GUEST.toLowerCase();
};

export const getClientPortalConfig = (
  clientName: string | null
): ClientPortalConfig => {
  if (!clientName) return CLIENT_PORTAL_CONFIG_MAP.default;

  const normalizedClientName = parseClientName(clientName).toLowerCase();
  return (
    CLIENT_PORTAL_CONFIG_MAP[normalizedClientName] ||
    CLIENT_PORTAL_CONFIG_MAP.default
  );
};

export interface GuestThemeColors {
  primaryColor: string;
  utilityBarBg: string;
  textColor: string;
  linkColor: string;
}

// Default UHC theme colors
const DEFAULT_GUEST_THEME_COLORS: GuestThemeColors = {
  primaryColor: '#002677',
  utilityBarBg: '#F3F3F3',
  textColor: '#002677',
  linkColor: '#196ECF',
};

/**
 * Extract guest theme colors from themeOverride or return defaults
 */
export const getGuestThemeColors = (themeOverride?: any): GuestThemeColors => {
  if (!themeOverride?.theme?.colors) {
    return DEFAULT_GUEST_THEME_COLORS;
  }

  const colors = themeOverride.theme.colors;
  return {
    primaryColor: colors.primary1 || DEFAULT_GUEST_THEME_COLORS.primaryColor,
    utilityBarBg:
      colors.infoFooterBackground || DEFAULT_GUEST_THEME_COLORS.utilityBarBg,
    textColor: colors.primary1 || DEFAULT_GUEST_THEME_COLORS.textColor,
    linkColor: colors.interactive1 || DEFAULT_GUEST_THEME_COLORS.linkColor,
  };
};
